<template>
    <div class="digital-signature-field">
        <template v-if="bothOptionsEnabled">
            <div class="signature-prompt mb-3">
                {{ $t('Please draw or upload your e-signature') }}
            </div>
            <div class="signature-mode-toggle" role="tablist" :aria-label="$t('Signature input method')">
                <button
                    type="button"
                    role="tab"
                    :aria-selected="signatureInputMode === 'draw'"
                    class="signature-mode-btn"
                    :class="{ 'signature-mode-btn--active': signatureInputMode === 'draw' }"
                    @click="setSignatureInputMode('draw')"
                >
                    {{ $t('Draw signature') }}
                </button>
                <button
                    type="button"
                    role="tab"
                    :aria-selected="signatureInputMode === 'upload'"
                    class="signature-mode-btn"
                    :class="{ 'signature-mode-btn--active': signatureInputMode === 'upload' }"
                    @click="setSignatureInputMode('upload')"
                >
                    {{ $t('Upload image') }}
                </button>
            </div>
        </template>

        <div v-if="showDrawUI" class="signature-wrap mt-2">
            <div class="signature-canvas-area">
                <canvas
                    ref="signatureCanvas"
                    class="signature-pad"
                    @mousedown="startDrawing"
                    @mousemove="draw"
                    @mouseup="endDrawing"
                    @mouseleave="endDrawing"
                    @touchstart.prevent="startDrawing"
                    @touchmove.prevent="draw"
                    @touchend.prevent="endDrawing"
                ></canvas>
                <v-btn icon small class="signature-reset-btn" :aria-label="$t('Clear signature')" @click="clearSignature">
                    <v-icon small color="grey darken-3">mdi-refresh</v-icon>
                </v-btn>
            </div>
            <div v-if="!bothOptionsEnabled" class="d-flex justify-end mt-2">
                <v-btn small text color="primary" @click="clearSignature">{{ $t('CLEAR') }}</v-btn>
            </div>
        </div>

        <div v-if="showUploadUI" class="signature-upload mt-2">
            <div
                class="signature-upload-dropzone"
                :class="{ 'signature-upload-dropzone--drag': dragOver }"
                role="button"
                tabindex="0"
                :aria-label="$t('Select signature image file')"
                @click="triggerFileInput"
                @keydown.enter.prevent="triggerFileInput"
                @keydown.space.prevent="triggerFileInput"
                @dragenter.prevent="dragOver = true"
                @dragover.prevent="dragOver = true"
                @dragleave.prevent="onDragLeave"
                @drop.prevent="onDrop"
            >
                <v-icon class="signature-upload-dropzone__icon" color="primary">mdi-tray-arrow-up</v-icon>
                <div class="signature-upload-dropzone__copy">
                    <span class="signature-upload-dropzone__cta" role="presentation">
                        {{ $t('Upload signature') }}
                    </span>
                    <p class="signature-upload-dropzone__hint">{{ $t('or drag and drop here') }}</p>
                </div>
            </div>
            <input
                ref="fileInput"
                type="file"
                accept="image/*"
                class="signature-upload-input"
                @change="onFileChange"
            />

            <div v-if="shouldShowUploadPreview" class="signature-upload-preview">
                <div class="signature-upload-preview__tile">
                    <div class="signature-upload-preview__toolbar">
                        <button
                            type="button"
                            class="signature-upload-preview__icon-btn"
                            :title="$t('Download')"
                            :aria-label="$t('Download')"
                            @click.stop="downloadSignatureFile"
                        >
                            <v-icon small class="signature-upload-preview__icon">mdi-download</v-icon>
                        </button>
                        <button
                            type="button"
                            class="signature-upload-preview__icon-btn"
                            :title="$t('Remove file')"
                            :aria-label="$t('Remove file')"
                            @click.stop="removeSignatureFile"
                        >
                            <v-icon small class="signature-upload-preview__icon">mdi-delete</v-icon>
                        </button>
                    </div>
                    <div class="signature-upload-preview__body">
                        <img
                            v-if="uploadPreviewImgSrc"
                            :src="uploadPreviewImgSrc"
                            alt=""
                            class="signature-upload-preview__thumb"
                        />
                        <span class="signature-upload-preview__ext">{{ fileExtensionLabel }}</span>
                    </div>
                </div>
                <div class="signature-upload-preview__filename" :title="signatureDisplayNameFull">
                    {{ truncatedDisplayName }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'DigitalSignatureField',
    props: {
        field: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            isDrawing: false,
            lastX: 0,
            lastY: 0,
            signatureInputMode: 'draw',
            dragOver: false,
            previewObjectUrl: null,
        };
    },
    computed: {
        /** Matches previous showSignaturePad behavior (draw canvas available). */
        legacyShowSignaturePad() {
            const hasDigitalFlag = this.field?.digitalSignature !== undefined;
            const hasAttachFlag = this.field?.attachFile !== undefined;
            if (hasDigitalFlag || hasAttachFlag) {
                const allowDigital = hasDigitalFlag ? this.coerceToggle(this.field.digitalSignature, 'digital-signature') : false;
                const allowAttach = hasAttachFlag ? this.coerceToggle(this.field.attachFile, 'attach-file') : false;
                return allowDigital || (!allowDigital && !allowAttach);
            }
            return true;
        },
        /** Matches previous showFileUpload behavior. */
        legacyShowFileUpload() {
            if (this.field?.attachFile !== undefined) {
                return this.coerceToggle(this.field.attachFile, 'attach-file');
            }
            return false;
        },
        bothOptionsEnabled() {
            return this.legacyShowSignaturePad && this.legacyShowFileUpload;
        },
        showDrawUI() {
            if (!this.legacyShowSignaturePad) return false;
            if (!this.bothOptionsEnabled) return true;
            return this.signatureInputMode === 'draw';
        },
        showUploadUI() {
            if (!this.legacyShowFileUpload) return false;
            if (!this.bothOptionsEnabled) return true;
            return this.signatureInputMode === 'upload';
        },
        fileExtensionLabel() {
            const name = this.field?.signatureFile?.name || '';
            if (name) {
                const i = name.lastIndexOf('.');
                if (i === -1 || i === name.length - 1) return 'IMG';
                return name.slice(i + 1).toUpperCase().slice(0, 5);
            }
            if (this.remoteSignatureUrl) {
                try {
                    const path = this.remoteSignatureUrl.split('?')[0];
                    const base = path.slice(path.lastIndexOf('/') + 1);
                    const i = base.lastIndexOf('.');
                    if (i !== -1 && i < base.length - 1) {
                        return base.slice(i + 1).toUpperCase().slice(0, 5);
                    }
                } catch (_e) {
                    /* ignore */
                }
            }
            return 'IMG';
        },
        /** Saved program / API sends hosted image URL — not a File; still show preview. */
        remoteSignatureUrl() {
            if (this.field?.signatureFile) return null;
            const v = this.field?.value;
            if (typeof v !== 'string') return null;
            const s = v.trim();
            if (!s || s.startsWith('data:')) return null;
            return /^https?:\/\//i.test(s) ? s : null;
        },
        shouldShowUploadPreview() {
            if (!this.showUploadUI) return false;
            if (this.field?.signatureFile) return true;
            return !!this.remoteSignatureUrl;
        },
        uploadPreviewImgSrc() {
            return this.previewObjectUrl || this.remoteSignatureUrl || null;
        },
        signatureDisplayNameFull() {
            if (this.field?.signatureFile?.name) return this.field.signatureFile.name;
            if (this.remoteSignatureUrl) {
                try {
                    const path = this.remoteSignatureUrl.split('?')[0];
                    const seg = decodeURIComponent(path.slice(path.lastIndexOf('/') + 1) || 'signature');
                    return seg || 'signature';
                } catch (_e) {
                    return 'signature';
                }
            }
            return '';
        },
        truncatedDisplayName() {
            const name = this.signatureDisplayNameFull;
            const max = 22;
            if (!name || name.length <= max) return name || '';
            return `${name.slice(0, max - 3)}...`;
        },
    },
    watch: {
        showDrawUI(visible) {
            if (visible) {
                this.$nextTick(() => this.restoreCanvasFromValue());
            }
        },
        showUploadUI(visible) {
            if (visible && this.field?.signatureFile) {
                this.$nextTick(() => this.attachPreviewForFile(this.field.signatureFile));
            }
        },
        remoteSignatureUrl(url) {
            if (url && this.showUploadUI && !this.field?.signatureFile) {
                this.revokePreview();
            }
        },
    },
    created() {
        this.signatureInputMode = this.inferInitialSignatureMode();
    },
    mounted() {
        this.$nextTick(() => {
            this.restoreCanvasFromValue();
            if (this.showUploadUI && this.field?.signatureFile) {
                this.attachPreviewForFile(this.field.signatureFile);
            }
        });
    },
    beforeDestroy() {
        this.revokePreview();
    },
    methods: {
        inferInitialSignatureMode() {
            if (!this.legacyShowSignaturePad && this.legacyShowFileUpload) return 'upload';
            if (this.legacyShowSignaturePad && !this.legacyShowFileUpload) return 'draw';
            const v = this.field?.value;
            if (typeof v === 'string' && v.startsWith('data:image')) return 'draw';
            if (this.field?.signatureFile || (typeof File !== 'undefined' && v instanceof File)) return 'upload';
            // Saved program detail: API stores hosted image URLs (e.g. S3) — preview lives in upload UI, not the canvas.
            if (typeof v === 'string') {
                const s = v.trim();
                if (s && /^https?:\/\//i.test(s)) return 'upload';
            }
            return 'draw';
        },
        setSignatureInputMode(mode) {
            if (!this.bothOptionsEnabled || mode === this.signatureInputMode) return;
            if (mode === 'upload') {
                this.clearCanvasOnly();
                this.$set(this.field, 'value', '');
            } else {
                this.clearUploadedSignatureFile();
            }
            this.signatureInputMode = mode;
        },
        clearCanvasOnly() {
            const canvas = this.getCanvas();
            if (canvas) {
                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, canvas.width, canvas.height);
            }
        },
        restoreCanvasFromValue() {
            if (!this.showDrawUI) return;
            const v = this.field?.value;
            if (typeof v !== 'string' || !v.startsWith('data:image')) return;
            const canvas = this.getCanvas();
            if (!canvas) return;
            this.ensureCanvasResolution(canvas);
            const ctx = canvas.getContext('2d');
            const rect = canvas.getBoundingClientRect();
            const w = rect.width;
            const h = rect.height;
            if (!w || !h) return;
            const img = new Image();
            img.onload = () => {
                ctx.clearRect(0, 0, w, h);
                ctx.drawImage(img, 0, 0, w, h);
            };
            img.src = v;
        },
        coerceToggle(value, token) {
            if (Array.isArray(value)) return token ? value.includes(token) : value.length > 0;
            if (typeof value === 'string') return value.toLowerCase() === 'true';
            return !!value;
        },
        getCanvas() {
            const ref = this.$refs.signatureCanvas;
            return Array.isArray(ref) ? ref[0] : ref;
        },
        getPointer(canvas, event) {
            const rect = canvas.getBoundingClientRect();
            const point = event.touches && event.touches[0] ? event.touches[0] : event;
            return {
                x: point.clientX - rect.left,
                y: point.clientY - rect.top,
            };
        },
        ensureCanvasResolution(canvas) {
            const dpr = window.devicePixelRatio || 1;
            const width = Math.floor(canvas.getBoundingClientRect().width);
            const height = Math.floor(canvas.getBoundingClientRect().height);
            if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
                canvas.width = width * dpr;
                canvas.height = height * dpr;
                const ctx = canvas.getContext('2d');
                ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            }
        },
        startDrawing(event) {
            if (!this.showDrawUI) return;
            const canvas = this.getCanvas();
            if (!canvas) return;
            this.ensureCanvasResolution(canvas);
            const ctx = canvas.getContext('2d');
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.lineWidth = 2;
            ctx.strokeStyle = '#222';
            const pointer = this.getPointer(canvas, event);
            this.isDrawing = true;
            this.lastX = pointer.x;
            this.lastY = pointer.y;
            ctx.beginPath();
            ctx.moveTo(pointer.x, pointer.y);
        },
        draw(event) {
            if (!this.isDrawing) return;
            const canvas = this.getCanvas();
            if (!canvas) return;
            const ctx = canvas.getContext('2d');
            const pointer = this.getPointer(canvas, event);
            ctx.beginPath();
            ctx.moveTo(this.lastX, this.lastY);
            ctx.lineTo(pointer.x, pointer.y);
            ctx.stroke();
            this.lastX = pointer.x;
            this.lastY = pointer.y;
        },
        endDrawing() {
            if (!this.isDrawing) return;
            const canvas = this.getCanvas();
            if (canvas && this.showDrawUI) {
                this.$delete(this.field, 'signatureFile');
                this.$set(this.field, 'value', canvas.toDataURL('image/png'));
            }
            this.isDrawing = false;
        },
        clearSignature() {
            this.clearCanvasOnly();
            this.$set(this.field, 'value', '');
        },
        triggerFileInput() {
            const input = this.$refs.fileInput;
            if (input && input.click) input.click();
        },
        onDragLeave(event) {
            if (!event.currentTarget.contains(event.relatedTarget)) {
                this.dragOver = false;
            }
        },
        onDrop(event) {
            this.dragOver = false;
            const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
            if (file && this.showUploadUI) {
                this.setUploadFile(file);
            }
        },
        revokePreview() {
            if (this.previewObjectUrl) {
                URL.revokeObjectURL(this.previewObjectUrl);
                this.previewObjectUrl = null;
            }
        },
        attachPreviewForFile(file) {
            if (!file || !(file instanceof File)) return;
            this.revokePreview();
            this.previewObjectUrl = URL.createObjectURL(file);
        },
        setUploadFile(file) {
            if (!file || !file.type || !file.type.startsWith('image/')) return;
            if (!this.showUploadUI) return;
            this.clearCanvasOnly();
            this.attachPreviewForFile(file);
            this.$set(this.field, 'signatureFile', file);
            this.$set(this.field, 'value', file);
        },
        clearUploadedSignatureFile() {
            this.revokePreview();
            this.$delete(this.field, 'signatureFile');
            this.$set(this.field, 'value', '');
            const input = this.$refs.fileInput;
            if (input) input.value = '';
        },
        removeSignatureFile() {
            if (!this.showUploadUI) return;
            this.clearUploadedSignatureFile();
        },
        downloadSignatureFile() {
            const file = this.field?.signatureFile;
            if (file) {
                const url = URL.createObjectURL(file);
                const a = document.createElement('a');
                a.href = url;
                a.download = file.name || 'signature';
                a.rel = 'noopener';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                return;
            }
            const remote = this.remoteSignatureUrl;
            if (remote) {
                window.open(remote, '_blank', 'noopener,noreferrer');
            }
        },
        onFileChange(event) {
            const file = event.target.files && event.target.files[0];
            if (file) {
                this.setUploadFile(file);
            }
            if (event.target) event.target.value = '';
        },
    },
};
</script>

<style scoped>
.digital-signature-field {
    width: 100%;
}

.signature-prompt {
    font-weight: 700;
    font-size: 1rem;
    color: rgba(0, 0, 0, 0.87);
    line-height: 1.35;
}

.signature-mode-toggle {
    display: flex;
    overflow: hidden;
}

.signature-mode-btn {
    flex: 1;
    padding: 10px 15px;
    font-size: 0.75rem;
    font-weight: 700;
    border-radius: 25px; 
    margin: 6px 4px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    border: none;
    cursor: pointer;
    background: #9e9e9e;
    color: #fff;
    transition: background 0.15s ease, color 0.15s ease;
}

.signature-mode-btn:focus {
    outline: 2px solid rgba(76, 175, 80, 0.6);
    outline-offset: 2px;
}

.signature-mode-btn--active {
    background: var(--primary-color);
    color: white;
}

.signature-canvas-area {
    position: relative;
}

.signature-pad {
    width: 100%;
    height: 120px;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    background: #fff;
    display: block;
    touch-action: none;
}

.signature-reset-btn {
    position: absolute;
    right: 4px;
    bottom: 4px;
    background: rgba(255, 255, 255, 0.92) !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.signature-upload {
    position: relative;
    width: 100%;
}

.signature-upload-input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

/* Sized to mirror `.attach-file-upload__dropzone` in StepOne (loan program fields) */
.signature-upload-dropzone {
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 14px;
    width: 100%;
    max-width: 100%;
    min-height: 72px;
    padding: 14px 16px;
    border: 2px dashed rgba(0, 0, 0, 0.55);
    border-radius: 10px;
    background: #fafafa;
    cursor: pointer;
    transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
}

.signature-upload-dropzone:hover {
    background: #f3f8ff;
    border-color: rgba(25, 118, 210, 0.55);
}

.signature-upload-dropzone:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.25);
}

.signature-upload-dropzone--drag {
    background: #e8f4fd;
    border-color: var(--primary-color, #1976d2);
    border-style: solid;
    box-shadow: inset 0 0 0 1px rgba(25, 118, 210, 0.15);
}

.signature-upload-dropzone__icon {
    flex-shrink: 0;
    font-size: 36px !important;
}

.signature-upload-dropzone__copy {
    flex: 1;
    min-width: 0;
    text-align: left;
}

.signature-upload-dropzone__cta {
    display: block;
    font-size: 0.9375rem;
    font-weight: 700;
    color: var(--primary-color, #1976d2);
    line-height: 1.3;
}

.signature-upload-dropzone:hover .signature-upload-dropzone__cta {
    text-decoration: underline;
    text-underline-offset: 3px;
}

.signature-upload-dropzone__hint {
    margin: 4px 0 0;
    font-size: 0.75rem;
    color: #9e9e9e;
    line-height: 1.4;
}

.signature-upload-preview {
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.signature-upload-preview__tile {
    position: relative;
    width: 88px;
    height: 88px;
    border-radius: 8px;
    background: #e8f5e9;
    overflow: hidden;
    box-shadow: 0 1px 4px rgba(27, 94, 32, 0.12);
}

.signature-upload-preview__toolbar {
    position: absolute;
    top: 3px;
    left: 3px;
    right: 3px;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: space-between;
    pointer-events: none;
}

.signature-upload-preview__icon-btn {
    pointer-events: auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    padding: 0;
    border: none;
    border-radius: 5px;
    background: rgba(255, 255, 255, 0.92);
    cursor: pointer;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    transition: background 0.15s ease, transform 0.15s ease;
}

.signature-upload-preview__icon-btn:hover {
    background: #fff;
    transform: scale(1.05);
}

.signature-upload-preview__icon {
    color: #1b5e20 !important;
}

.signature-upload-preview__body {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.signature-upload-preview__thumb {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.35;
}

.signature-upload-preview__ext {
    position: relative;
    z-index: 1;
    font-size: 1.1rem;
    font-weight: 800;
    letter-spacing: 0.02em;
    color: #1b5e20;
    text-shadow: 0 0 1px #fff, 0 1px 2px rgba(255, 255, 255, 0.9);
}

.signature-upload-preview__filename {
    margin-top: 6px;
    max-width: 200px;
    font-size: 0.75rem;
    font-weight: 600;
    color: #2e7d32;
    line-height: 1.3;
    word-break: break-all;
}
</style>
